/**
 * C.12 polish — NIPE intro balloon.
 *
 * Renders a real clippy-style speech balloon attached to the sprite (NOT
 * the modal panel). Uses the original clippyjs balloon CSS (clippy.css
 * .clippy-balloon / .clippy-tip / .clippy-top-right) so the bubble has
 * the authentic yellow-paper look + tip art pointing down-right at the
 * sprite.
 *
 * Behavior:
 *   - Mounts when the panel opens; shows the intro line.
 *   - After 10s of no keystroke in the chat input, swaps to a nudge.
 *   - Disposed via createPortal to document.body so it floats above
 *     everything else.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { testIds } from '../../lib/testIds';

const INTRO_TEXT =
  'Hi, I\'m NIPE, short for "Non-Infringing Paperclip Entity." Click on any of the topics above to learn more about features.';
const NUDGE_TEXT =
  "What are you, shy? Talk to me, I've got context on everything here.";
const NUDGE_DELAY_MS = 10_000;

export interface NipeIntroBalloonProps {
  /** Signal the user has typed something — cancels the nudge timer. */
  userHasTyped?: boolean;
  /**
   * Override the body text. Used by the 5000ms greet-bubble path
   * (NipeOverlay) so we reuse the same balloon styling for both the
   * panel-open intro AND the standalone greet. When omitted, the
   * component runs its classic intro → nudge cycle.
   */
  overrideText?: string;
  /** Override the data-testid so multiple instances can be selected. */
  testId?: string;
  /**
   * F18.G.1 — When provided, render an explicit close (×) button.
   * Used by the sticky portal-welcome balloon, which doesn't auto-
   * dismiss on next-click and needs a user-driven escape hatch.
   */
  onClose?: () => void;
}

export function NipeIntroBalloon({
  userHasTyped = false,
  overrideText,
  testId,
  onClose,
}: NipeIntroBalloonProps): ReactElement | null {
  const [text, setText] = useState(overrideText ?? INTRO_TEXT);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (overrideText !== undefined) {
      // Caller controls the text; skip the intro→nudge cycle.
      setText(overrideText);
      return;
    }
    if (userHasTyped) {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = window.setTimeout(() => {
      setText(NUDGE_TEXT);
    }, NUDGE_DELAY_MS);
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [userHasTyped, overrideText]);

  // Anchor: directly above the sprite (bottom-right). The clippy.css
  // tip variants choose the tip art. We use top-right because the
  // balloon sits ABOVE the sprite, leaning into its LEFT side.
  if (typeof document === 'undefined') return null;
  // Position: just LEFT of the sprite. Sprite is at right:16
  // (~124px wide), so we anchor bubble to right: 16 + 124 + 20 = 160
  // from the right edge, vertically centered on the sprite's head.
  // Tail (custom CSS triangle, no broken sprite art) sticks out the
  // bubble's right side pointing toward the sprite's head.
  return createPortal(
    <div
      data-testid={testId ?? testIds.nipe.bubble}
      className="nipe-intro-balloon"
      style={{
        position: 'fixed',
        right: 'calc(var(--clippy-right-offset, 16px) + 144px)',
        bottom: 'calc(var(--taskbar-height, 36px) + var(--clippy-bottom-offset, 8px) + 32px)',
        maxWidth: onClose ? 340 : 280,
        minWidth: 220,
        zIndex: 2055,
        background: '#ffe',
        border: '2px solid #000',
        borderRadius: 12,
        padding: '10px 14px',
        boxShadow: '3px 3px 0 rgba(0,0,0,0.35)',
        fontFamily: 'var(--xp-font-body)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {onClose && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: 4,
            right: 6,
            width: 18,
            height: 18,
            minWidth: 18,
            minHeight: 18,
            padding: 0,
            background: 'transparent',
            border: 'none',
            fontSize: 14,
            lineHeight: '14px',
            cursor: 'pointer',
            color: '#000',
            boxShadow: 'none',
          }}
        >
          ×
        </button>
      )}
      <div style={{ color: '#000', fontSize: 12, lineHeight: 1.4, paddingRight: onClose ? 16 : 0 }}>{text}</div>
      {/* Two-triangle tail pointing right-down at the sprite. Outer
          triangle = black border. Inner triangle (offset 2px) = bubble
          fill. */}
      <div
        style={{
          position: 'absolute',
          right: -22,
          bottom: 12,
          width: 0,
          height: 0,
          borderTop: '10px solid transparent',
          borderBottom: '14px solid transparent',
          borderLeft: '22px solid #000',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: -18,
          bottom: 14,
          width: 0,
          height: 0,
          borderTop: '8px solid transparent',
          borderBottom: '12px solid transparent',
          borderLeft: '18px solid #ffe',
          pointerEvents: 'none',
        }}
      />
    </div>,
    document.body,
  );
}
